import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import * as syncProtocol from 'y-protocols/sync';

import { getSpotFromSearch } from '@canvas/branch-deploy';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { MessageType } from '@canvas/protocol';
import { encodeMessageV2 } from '@canvas/protocol/v2';
import { YjsOrigin, YJS_DEFAULT } from '@canvas/transaction';

import { type RelaySupplierResult } from '../network/socket.ts';
import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
  createWebSocketTransport,
} from '../transport/index.ts';

import { type CollabStrategy } from './collab-strategy.ts';

export class CfCollabStrategy implements CollabStrategy {
  readonly type = 'cf' as const;

  createTransport(ctx: TransportContext, callbacks: TransportCallbacks): Transport {
    return createWebSocketTransport(ctx, callbacks);
  }

  sendAwareness(
    message: Uint8Array<ArrayBuffer>,
    _transport: Transport | undefined,
    fallbackSend: (msg: Uint8Array<ArrayBuffer>) => void,
  ): void {
    fallbackSend(message);
  }

  getStepsUrl(params: {
    documentId: string;
    headStep: string;
    serverUrl?: string;
    attempt: number;
  }): string {
    const { documentId, headStep, attempt } = params;
    const relayBaseUrl = this.getCanvasRelayBaseUrl(attempt);
    return `${relayBaseUrl}/api/public/document/${encodeURIComponent(
      documentId,
    )}/steps/${headStep}`;
  }

  getRelayUrl(params: {
    boardId: string;
    attempt: number;
    resourceType: 'whiteboard' | 'database';
    tenantId: string;
    activationId: string;
  }): RelaySupplierResult {
    const { boardId, attempt } = params;

    const relaySpot =
      typeof globalThis.location !== 'undefined'
        ? getSpotFromSearch(globalThis.location.search, 'canvas-relay')
        : undefined;

    const relayUrl =
      typeof relaySpot === 'number'
        ? `wss://canvas-relay-canvas-relay-${relaySpot}.atlassian-workers-test.workers.dev`
        : process.env.VITE_CANVAS_RELAY_WSS_URL;

    let baseUrl: string | undefined;
    let prefix = '';
    if (
      isFeatureFlagEnabled('useConnectionFallback') &&
      attempt > 2 &&
      typeof globalThis.location !== 'undefined'
    ) {
      const parentUrl = new URL(globalThis.location.href);
      const wsProtocol = parentUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      baseUrl = `${wsProtocol}//${parentUrl.host}`;
      prefix = '/canvas-collab/backup';
    } else {
      baseUrl = relayUrl;
    }

    if (!baseUrl) {
      throw new Error('No relay URL found in current environment');
    }

    const url = new URL(baseUrl);
    url.pathname = `${prefix}/api/public/document/${encodeURIComponent(boardId)}/ws`;
    url.searchParams.set('closeOnMissingUpdate', 'true');

    return {
      url: url.href,
    };
  }

  isReceivedMessageValid(): boolean {
    return true;
  }

  configureUpdateBatching(): void {
    // CF sends batched updates directly over the WebSocket; no StateBridge needed.
  }

  sendConnectSync({
    canSync,
    sendSyncInit,
  }: Parameters<CollabStrategy['sendConnectSync']>[0]) {
    if (canSync) {
      sendSyncInit();
    }
  }

  onSyncPhase0(
    { data: { request, recipient } }: Parameters<CollabStrategy['onSyncPhase0']>[0],
    { doc, origin, maxMessageSize, send }: Parameters<CollabStrategy['onSyncPhase0']>[1],
  ): void {
    const encoder = encoding.createEncoder();
    syncProtocol.readSyncMessage(
      decoding.createDecoder(request),
      encoder,
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    const syncReplyEncoder = encoding.createEncoder();
    syncProtocol.writeSyncStep1(syncReplyEncoder, doc);
    const message = encodeMessageV2({
      type: MessageType.SyncPhase1,
      data: {
        recipient: recipient,
        request: encoding.toUint8Array(syncReplyEncoder),
        response: encoding.toUint8Array(encoder),
      },
    });
    if (message.length > maxMessageSize) {
      return;
    }
    send(message);
  }

  onSyncPhase1(
    {
      data: { request, response, recipient },
    }: Parameters<CollabStrategy['onSyncPhase1']>[0],
    {
      doc,
      origin,
      maxMessageSize,
      send,
      completeLazySync,
    }: Parameters<CollabStrategy['onSyncPhase1']>[1],
  ): void {
    const encoder = encoding.createEncoder();
    syncProtocol.readSyncMessage(
      decoding.createDecoder(request),
      encoder,
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    syncProtocol.readSyncMessage(
      decoding.createDecoder(response),
      encoding.createEncoder(),
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    completeLazySync();
    const message = encodeMessageV2({
      type: MessageType.SyncPhase2,
      data: { recipient, response: encoding.toUint8Array(encoder) },
    });
    if (message.length > maxMessageSize) {
      return;
    }
    send(message);
  }

  onSyncPhase2(
    { data: { response } }: Parameters<CollabStrategy['onSyncPhase2']>[0],
    { doc, origin, resetLazySyncState }: Parameters<CollabStrategy['onSyncPhase2']>[1],
  ): void {
    syncProtocol.readSyncMessage(
      decoding.createDecoder(response),
      encoding.createEncoder(),
      doc,
      new YjsOrigin(YJS_DEFAULT, origin),
    );
    resetLazySyncState();
  }

  onUpdateApplied({
    triggerLazySync,
  }: Parameters<CollabStrategy['onUpdateApplied']>[0]): void {
    triggerLazySync();
  }

  onLazySyncTimeout({
    onTimeout,
  }: Parameters<CollabStrategy['onLazySyncTimeout']>[0]): void {
    onTimeout();
  }

  shouldHandleLazySync(): boolean {
    return false;
  }

  sendLazySyncRequest({
    message,
    send,
  }: Parameters<CollabStrategy['sendLazySyncRequest']>[0]): void {
    send(message);
  }

  shouldDeferReconnectUpdate(): boolean {
    return false;
  }

  prepareReconnectUpdateChanges(): void {
    // CF does not need to suppress StateBridge observers during reconnect.
  }

  restoreReconnectUpdateChanges(): void {
    // CF has no reconnect-time StateBridge observer to restore.
  }

  prepareReconnectUpdate(): void {
    // CF sends the reconnect diff payload directly; no bridge staging needed.
  }

  resetModeState(): void {
    // CF has no mode-local bridge state to reset.
  }

  private getCanvasRelayBaseUrl(attempt: number): string | undefined {
    const relaySpot =
      typeof globalThis.location !== 'undefined'
        ? getSpotFromSearch(globalThis.location.search, 'canvas-relay')
        : undefined;

    if (
      isFeatureFlagEnabled('useConnectionFallback') &&
      attempt > 2 &&
      typeof globalThis.location !== 'undefined'
    ) {
      const parent = new URL(globalThis.location.href);
      const base = `${parent.protocol}//${parent.host}`;
      return `${base}/canvas-collab/backup`;
    }

    const relayBaseUrl =
      typeof relaySpot === 'number'
        ? `https://canvas-relay-canvas-relay-${relaySpot}.atlassian-workers-test.workers.dev`
        : process.env.VITE_CANVAS_RELAY_URL;

    return relayBaseUrl;
  }
}
